// src/modules/subscription/pages/PlanEdit.jsx
import React, { useEffect, useState } from "react"
import { Card, Button, Form, Row, Col, Spinner, Alert } from "react-bootstrap"
import { useNavigate, useParams } from "react-router-dom"
import Layout from "../../../layout/default"
import Block from "../../../components/Block/Block"
import { Icon } from "../../../components"
import planService from "../services/planService"

/* ────────────────────────────────────────────────────────────────
 *  Estado inicial del formulario
 * ──────────────────────────────────────────────────────────────── */
const emptyPlan = {
  code: "",
  name: "",
  base_price: 0,
  description: "",
  active: true,
}

const PlanEdit = () => {
  /* ───────── routing ────────────────────────────── */
  const { id: rawId }  = useParams()
  const isNew          = !rawId || rawId === "new"
  const planId         = isNew ? null : rawId
  const navigate       = useNavigate()

  /* ───────── estado ─────────────────────────────── */
  const [item, setItem]       = useState(emptyPlan)
  const [loading, setLoading] = useState(Boolean(planId))
  const [saving, setSaving]   = useState(false)
  const [error, setError]     = useState(null)

  /* ───────── carga del registro ─────────────────── */
  useEffect(() => {
    if (!planId) {
      setLoading(false)
      return
    }

    let mounted = true
    ;(async () => {
      setLoading(true)
      setError(null)
      try {
        const data = await planService.get(planId)
        if (mounted) setItem(data)
      } catch (err) {
        console.error("Error fetching plan", err)
        if (mounted) setError("No se pudo cargar el plan.")
      } finally {
        if (mounted) setLoading(false)
      }
    })()

    return () => { mounted = false }
  }, [planId])

  /* ───────── handlers ───────────────────────────── */
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target
    setItem(prev => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setSaving(true)
    setError(null)
    try {
      if (isNew)       await planService.create(item)
      else if (planId) await planService.update(planId, item)

      navigate("/plan")
    } catch (err) {
      console.error("Error saving plan", err)
      setError("No se pudo guardar el plan.")
    } finally {
      setSaving(false)
    }
  }

  /* ────────────────────────────────────────────────
   *  Render
   * ──────────────────────────────────────────────── */
  return (
    <Layout
      title={isNew ? "Nuevo Plan" : `Editar Plan #${planId}`}
      content="container"
      loading={loading}
    >
      {/* ─── Encabezado de página ───────────────────── */}
      <Block.Head>
        <Block.HeadBetween>
          <Block.HeadContent>
            <Block.Title tag="h2">
              {isNew ? "Crear Plan" : `Editar Plan #${planId}`}
            </Block.Title>
          </Block.HeadContent>
        </Block.HeadBetween>
      </Block.Head>

      {/* ─── Formulario ─────────────────────────────── */}
      <Block>
        <Card body>
          {error && <Alert variant="danger">{error}</Alert>}

          {loading ? (
            <div className="text-center py-4">
              <Spinner animation="border" />
            </div>
          ) : (
            <Form onSubmit={handleSubmit}>
              <Row className="g-3">
                {/* Código */}
                <Col md={4}>
                  <Form.Group>
                    <Form.Label>Código</Form.Label>
                    <Form.Control
                      name="code"
                      value={item.code}
                      onChange={handleChange}
                      required
                    />
                  </Form.Group>
                </Col>

                {/* Nombre */}
                <Col md={8}>
                  <Form.Group>
                    <Form.Label>Nombre</Form.Label>
                    <Form.Control
                      name="name"
                      value={item.name}
                      onChange={handleChange}
                      required
                    />
                  </Form.Group>
                </Col>

                {/* Precio */}
                <Col md={4}>
                  <Form.Group>
                    <Form.Label>Precio €/mes</Form.Label>
                    <Form.Control
                      type="number"
                      name="base_price"
                      step="0.01"
                      min="0"
                      value={item.base_price}
                      onChange={handleChange}
                      required
                    />
                  </Form.Group>
                </Col>

                {/* Descripción */}
                <Col md={8}>
                  <Form.Group>
                    <Form.Label>Descripción</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={3}
                      name="description"
                      value={item.description}
                      onChange={handleChange}
                    />
                  </Form.Group>
                </Col>

                {/* Activo */}
                <Col md={12} className="d-flex align-items-center">
                  <Form.Check
                    type="checkbox"
                    label="Activo"
                    name="active"
                    checked={item.active}
                    onChange={handleChange}
                  />
                </Col>

                {/* Botón guardar */}
                <Col xs={12} className="text-end">
                  <Button variant="primary" type="submit" disabled={saving}>
                    {saving ? (
                      <>
                        <Spinner
                          as="span"
                          animation="border"
                          size="sm"
                          className="me-2"
                        />
                        Guardando…
                      </>
                    ) : (
                      <>
                        <Icon name="save" />
                        <span> Guardar</span>
                      </>
                    )}
                  </Button>
                </Col>
              </Row>
            </Form>
          )}
        </Card>
      </Block>
    </Layout>
  )
}

export default PlanEdit
